import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient } from '@/shared/services/queryClient';
import { mobileAccessApi, type CustomerAccessRole, type InviteCustomerMember } from '../services/mobileAccessApi';

const key = (customerId: string) => ['customers', customerId, 'mobile-access'];
const membersKey = (customerId: string) => ['customers', customerId, 'mobile-members'];

export const useMobileAccessStatus = (customerId: string, enabled = true) =>
  useQuery({
    queryKey: key(customerId),
    queryFn: () => mobileAccessApi.getStatus(customerId),
    enabled: !!customerId && enabled,
  });

export const useEnableMobileAccess = (customerId: string) =>
  useMutation({
    mutationFn: () => mobileAccessApi.enable(customerId),
    onSuccess: (data) => queryClient.setQueryData(key(customerId), data),
  });

export const useResendMobileInvite = (customerId: string) =>
  useMutation({
    mutationFn: () => mobileAccessApi.resend(customerId),
    onSuccess: (data) => queryClient.setQueryData(key(customerId), data),
  });

export const useRevokeMobileAccess = (customerId: string) =>
  useMutation({
    mutationFn: (reason?: string) => mobileAccessApi.revoke(customerId, reason),
    onSuccess: (data) => queryClient.setQueryData(key(customerId), data),
  });

export const useCustomerMembers = (customerId: string, pageNumber = 1, pageSize = 25, enabled = true) =>
  useQuery({
    queryKey: [...membersKey(customerId), pageNumber, pageSize],
    queryFn: () => mobileAccessApi.getMembers(customerId, pageNumber, pageSize),
    enabled: !!customerId && enabled,
  });

export const useInviteCustomerMember = (customerId: string) =>
  useMutation({
    mutationFn: (payload: InviteCustomerMember) => mobileAccessApi.inviteMember(customerId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: membersKey(customerId) }),
  });

export const useChangeCustomerMemberRole = (customerId: string) =>
  useMutation({
    mutationFn: ({ membershipId, accessRole }: { membershipId: string; accessRole: CustomerAccessRole }) =>
      mobileAccessApi.changeMemberRole(customerId, membershipId, accessRole),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: membersKey(customerId) }),
  });

export const useCustomerMemberAction = (customerId: string) =>
  useMutation({
    mutationFn: ({ membershipId, action, reason }: {
      membershipId: string;
      action: 'resend-invitation' | 'suspend' | 'reactivate' | 'revoke' | 'reinvite';
      reason?: string;
    }) => mobileAccessApi.memberAction(customerId, membershipId, action, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: membersKey(customerId) }),
  });
